/**
 * A custom hook that generates a query using react-query's useQuery.
 * 
 * @param key - The key to use for the query cache. This will be wrapped in an array.
 * @param queryFunction - A function that returns a Promise which resolves to the query data.
 * @returns The query object from react-query's useQuery with the provided configuration.
 * 
 * ```
 */
import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import _ from "lodash";

interface QueryConfig {
  queryKey: string[];
  queryFn: () => Promise<any>;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
  refetchOnMount?: boolean | "always";
  select?: (data: any) => any;
  enabled?: boolean;
  retry?: number | boolean;
}

export const useQueryBuilder = (
  cacheKey: string[],
  fetchFunction: () => Promise<any>,
  refetchOnWindowFocus = false,
  staleTime = 1 * 60 * 1000,
  refetchOnMount: boolean | "always" = false,
  select?: (data: any) => any,
  enabled = true,
  retry: number | boolean = 3,
) => {
  const queryConfig: QueryConfig = {
    queryKey: cacheKey,
    queryFn: fetchFunction,
    refetchOnWindowFocus,
    staleTime,
    refetchOnMount,
    select,
    enabled,
    retry,
  };

  return useQuery(queryConfig);
};

export const prefetchBuilder = async <T>(
  queryClient: QueryClient,
  cacheKey: string[],
  fetchFunction: () => Promise<T>
) => {
  await queryClient.prefetchQuery({
    queryKey: cacheKey,
    queryFn: fetchFunction,
  });
};

export const useInfiniteQueryBuilder = (
  queryKey: string[],
  queryFn: ({ pageParam }: { pageParam: any }) => Promise<any>,
  initialPageParam: any,
  getNextPageParam: (lastPage: any, allPages: any[]) => any,
  getPreviousPageParam?: (firstPage: any, allPages: any[]) => any,
  enabled = true,
  refetchOnWindowFocus = true,
  retry: number | boolean = 3,
) => {

  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam,
    getNextPageParam,
    getPreviousPageParam,
    enabled,
    refetchOnWindowFocus,
    retry,
  });

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = queryResult;

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const hasRecords = _.get(data, "pages[0].totalRecordsCount", 0) > 0;

  return {
    ...queryResult,
    loadMore,
    hasRecords
  };
};

interface MutationConfig<TData = unknown, TVariables = unknown> {
  mutationKey: string[];
  mutationFn: (variables: TVariables) => Promise<TData>;
}

export const useMutationBuilder = <TData = unknown, TVariables = unknown>(
  cacheKey: string[],
  mutationFunction: (variables: TVariables) => Promise<TData>,
  invalidateQueryKeys: string[] = []
) => {
  const queryClient = useQueryClient();

  const mutationConfig: MutationConfig<TData, TVariables> = {
    mutationKey: cacheKey,
    mutationFn: mutationFunction,
  };

  return useMutation({
    ...mutationConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueryKeys });
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: invalidateQueryKeys });
    }
  });
};
